import { LICENSE_MODULE } from "@selector/shared";
import type { Configuration } from "@selector/shared/graphql";
import { type FC, useCallback, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { useNotificationSummaryViewUpdate } from "@/app-utils/hooks/useNotificationUpdate.ts";
import { isLicensed } from "@/redux/modules/config.js";
import { getPlaybookEventName, type IEventState, loadEvent } from "@/redux/modules/event.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { ENTITY_TYPE } from "@/settings/settings.js";
import EventRelatedSamplesView from "@/ui-modules/entities/EventRelatedSamplesView.js";
import EventWorkflowView from "@/ui-modules/entities/EventWorkflowView.tsx";
import { useGetPlaybookEventRelatedSamplesCountsQuery } from "@/ui-modules/playbooks/events/graphql/event-queries.gql.js";

import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import type { IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import EventSummaryView from "./EventSummaryView.js";

const EventDetailsPage: FC = () => {
  // redux
  const dispatch = useDispatch<ReduxDispatch>();
  const { event, isLoading } = useSelector<IAppState, IEventState>((state) => state.event, shallowEqual);
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration, shallowEqual);
  // router
  const { eventId } = useParams<"eventId">();
  const refetchEvent = useCallback(() => dispatch(loadEvent(Number(eventId))), [dispatch, eventId]);
  useNotificationSummaryViewUpdate(refetchEvent, Number(eventId));

  // Required to disable/hide the "Related Samples" tab
  const { loading, data } = useGetPlaybookEventRelatedSamplesCountsQuery({
    variables: { eventId: Number(eventId) },
  });

  const { displayedSampleCount, totalSampleCount } = data?.playbook?.event ?? {};

  useEffect(() => {
    void dispatch(loadEvent(Number(eventId)));
  }, [dispatch, eventId]);

  const getNavItems = useCallback((): IRouteNavItemProps[] => {
    const navItems: IRouteNavItemProps[] = [
      {
        label: "Summary",
        route: "summary",
        pageComponent: <EventSummaryView />,
        default: true,
      },
    ];

    if (
      totalSampleCount != undefined &&
      displayedSampleCount != undefined &&
      isLicensed(configuration, LICENSE_MODULE.TRACKER)
    ) {
      navItems.push({
        label:
          totalSampleCount == displayedSampleCount
            ? "Samples"
            : `Samples (${displayedSampleCount} of ${totalSampleCount})`,
        route: "samples",
        pageComponent: <EventRelatedSamplesView />,
        default: true,
        disabled: displayedSampleCount === 0,
        tooltip:
          totalSampleCount > 0 && totalSampleCount != displayedSampleCount
            ? "Some of the samples may have been deleted, or you may not have permission to view them"
            : undefined,
        disabledTooltip:
          totalSampleCount == 0
            ? "No related samples to display"
            : "Some of the samples may have been deleted, or you may not have permission to view them",
      });
    }

    if (event?.workflowP2ExtendedConfig != null) {
      navItems.push({
        label: "Preview",
        route: "preview",
        pageComponent: <EventWorkflowView />,
        default: true,
        disabled: !event?.workflowP2ExtendedConfig,
        disabledTooltip: "Preview is only available for Processor Playbooks",
      });
    }
    return navItems;
  }, [configuration, event, totalSampleCount, displayedSampleCount]);

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.EVENT}
      entityId={Number(eventId)}
      getDisplayName={() => getPlaybookEventName(event)}
      sidebarNavItems={getNavItems()}
      isLoading={isLoading || loading}
    />
  );
};

export { EventDetailsPage };
